import { Plane, Home as HomeIcon, Languages, HeartHandshake, Footprints, ShieldCheck, Quote, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BtnLink, Eyebrow, Reveal, SectionTitle } from "@/components/site/Primitives";
import { CheckList, InlineLink } from "@/components/site/Blocks";
import { FounderPhoto } from "@/components/site/FounderPhoto";
import { useLink, useT } from "@/lib/i18n";

const PathCard = ({ data, to, img, Icon, dark, testid }) => (
  <article data-testid={testid} className={`group flex h-full flex-col overflow-hidden rounded-[32px] ${dark ? "bg-forest text-ivory" : "card-soft text-forest"}`}>
    <div className="relative h-64 overflow-hidden sm:h-72">
      <img src={img} alt={data.imageAlt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
      <svg className={`absolute -bottom-px left-0 w-full ${dark ? "text-forest" : "text-ivory-card"}`} viewBox="0 0 400 40" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 40V20C80 0 160 0 240 18s120 18 160 0v22z" fill="currentColor" />
      </svg>
    </div>
    <div className="relative flex flex-1 flex-col px-7 pb-9 sm:px-10">
      <span className={`-mt-10 mb-6 flex h-16 w-16 items-center justify-center rounded-full border-4 ${dark ? "border-forest bg-gold-deep text-white" : "border-ivory-card bg-forest text-gold-light"}`}>
        <Icon size={24} strokeWidth={1.5} />
      </span>
      <h3 className="font-display text-3xl leading-tight sm:text-[2.3rem]">{data.title}</h3>
      <p className={`mt-3 text-[15px] leading-relaxed ${dark ? "text-ivory/80" : "text-forest/75"}`}>{data.desc}</p>
      <div className="mt-6 flex-1"><CheckList items={data.items} light={dark} /></div>
      <BtnLink to={to} variant={dark ? "ivory" : "forest"} className="mt-9 self-start" data-testid={`${testid}-btn`}>{data.cta}</BtnLink>
    </div>
  </article>
);

export const PathCards = () => {
  const t = useT();
  const link = useLink();
  return (
    <section data-testid="paths-section" className="container-x py-20 sm:py-28">
      <Reveal><SectionTitle eyebrow={t.paths.eyebrow} title={t.paths.title} center /></Reveal>
      <div className="mt-14 grid gap-7 md:grid-cols-2">
        <Reveal><PathCard data={t.paths.a} to={link("moving")} img="/img/ljubljana.jpg" Icon={Plane} testid="path-card-moving" /></Reveal>
        <Reveal delay={0.1}><PathCard data={t.paths.b} to={link("living")} img="/img/izola-boats.jpg" Icon={HomeIcon} dark testid="path-card-living" /></Reveal>
      </div>
      {t.paths.businessNote && (
        <p className="mt-10 text-center">
          <Link to={link("business")} data-testid="paths-business-link" className="text-[15px] font-medium text-forest underline decoration-gold-text/50 underline-offset-4 transition-colors hover:text-gold-text">
            {t.paths.businessNote}
          </Link>
        </p>
      )}
    </section>
  );
};

const WHY_ICONS = [Languages, HeartHandshake, Footprints, ShieldCheck];

export const Why = () => {
  const t = useT();
  return (
    <section data-testid="why-section" className="bg-ivory-2 py-20 sm:py-28">
      <div className="container-x">
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5"><SectionTitle eyebrow={t.why.eyebrow} title={t.why.title} /></Reveal>
          <div className="grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:col-span-7">
            {t.why.items.map((it, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={it.title} delay={i * 0.06}>
                  <div className="border-t border-forest/20 pt-7" data-testid={`why-item-${i}`}>
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-forest text-gold-light"><Icon size={22} strokeWidth={1.5} /></span>
                    <h3 className="mt-6 font-display text-[1.75rem] leading-tight text-forest">{it.title}</h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-forest/75">{it.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
        <p className="mt-14 max-w-3xl text-sm text-forest/70" data-testid="why-disclaimer">{t.why.note}</p>
      </div>
    </section>
  );
};

export const FounderSection = () => {
  const t = useT();
  const link = useLink();
  const f = t.founder;
  return (
    <section data-testid="founder-section" className="container-x py-20 sm:py-32">
      <div className="grid items-center gap-16 lg:grid-cols-12">
        <Reveal className="relative lg:col-span-5">
          <FounderPhoto className="aspect-[4/5] w-full max-w-[460px]" testid="founder-section-photo" />
          <img src="/img/bled-boat.jpg" alt="" loading="lazy" className="absolute -top-6 right-0 h-36 w-36 rounded-full border-[6px] border-ivory object-cover shadow-xl sm:h-52 sm:w-52 lg:-right-8" />
        </Reveal>
        <div className="lg:col-span-7 lg:pl-8">
          <Reveal><Eyebrow>{f.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.06}><h2 className="mt-5 font-display text-5xl italic leading-none text-forest sm:text-6xl lg:text-7xl" data-testid="founder-title">{f.title}</h2></Reveal>
          <Reveal delay={0.12}>
            <div className="mt-8 space-y-5 text-base leading-relaxed text-forest/80 sm:text-lg">
              {f.paragraphs.map((p) => <p key={p}>{p}</p>)}
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <dl className="mt-10 grid grid-cols-3 gap-4 border-y border-forest/15 py-7">
              {f.facts.map((x) => (
                <div key={x.k}>
                  <dt className="font-display text-3xl text-gold-text sm:text-4xl">{x.k}</dt>
                  <dd className="mt-1 text-xs leading-snug text-forest/75 sm:text-sm">{x.v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-8"><InlineLink to={link("about")} testid="founder-about-link">{f.cta} <ArrowUpRight size={16} /></InlineLink></div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const HomeOffice = () => {
  const t = useT();
  const link = useLink();
  const o = t.homeOffice;
  return (
    <section data-testid="home-office-section" className="bg-forest py-20 text-ivory sm:py-28">
      <div className="container-x grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-7">
          <img
            src="/img/sejna-soba-izola.webp"
            alt={o.alt}
            width="1179"
            height="1176"
            loading="lazy"
            data-testid="home-office-photo"
            className="h-auto w-full rounded-[32px] shadow-2xl"
          />
        </Reveal>
        <div className="lg:col-span-5">
          <Reveal><Eyebrow light>{o.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.06}><h2 data-testid="home-office-title" className="mt-5 font-display text-4xl leading-tight text-ivory sm:text-5xl">{o.title}</h2></Reveal>
          <Reveal delay={0.12}>
            <p data-testid="home-office-text" className="mt-6 text-base leading-relaxed text-ivory/85 sm:text-lg">{o.text}</p>
            <p data-testid="home-office-address" className="mt-8 border-t border-ivory/20 pt-6 text-lg font-semibold text-ivory">{o.address}</p>
            <BtnLink to={link("contact")} className="mt-8" data-testid="home-office-cta">{o.cta}</BtnLink>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const ServicesOverview = () => {
  const t = useT();
  const link = useLink();
  const s = t.services;
  return (
    <section data-testid="services-section" className="bg-ivory-card py-20 sm:py-28">
      <div className="container-x">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <Reveal><SectionTitle eyebrow={s.eyebrow} title={s.title} /></Reveal>
          <Reveal delay={0.08}><p className="max-w-md text-[15px] leading-relaxed text-forest/75">{s.intro}</p></Reveal>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3">
          {s.items.map((it, i) => (
            <Reveal key={it.key} delay={(i % 3) * 0.06}>
              <Link to={link(it.to)} data-testid={`service-${it.key}`} className="group flex h-full flex-col border-t border-forest/15 py-9 pr-6 transition-colors md:pr-10">
                <span className="font-display text-xl italic text-gold-text">0{i + 1}</span>
                <h3 className="mt-4 font-display text-[1.75rem] leading-tight text-forest transition-colors group-hover:text-gold-text">{it.title}</h3>
                <p className="mt-3 flex-1 text-[15px] leading-relaxed text-forest/75">{it.text}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-forest">
                  {it.cta} <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Faq = () => {
  const t = useT();
  const f = t.faq;
  return (
    <section data-testid="faq-section" className="bg-ivory-2 py-20 sm:py-28">
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-4"><SectionTitle eyebrow={f.eyebrow} title={f.title} /></Reveal>
        <Accordion type="single" collapsible className="lg:col-span-8">
          {f.items.map((it, i) => (
            <AccordionItem key={it.q} value={`faq-${i}`} className="border-forest/15">
              <AccordionTrigger data-testid={`faq-q-${i}`} className="py-6 text-left font-display text-2xl leading-snug text-forest hover:no-underline sm:text-[1.7rem]">{it.q}</AccordionTrigger>
              <AccordionContent className="pb-6 pr-8 text-base leading-relaxed text-forest/80">{it.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const t = useT();
  const s = t.testimonials;
  return (
    <section data-testid="testimonials-section" className="relative overflow-hidden bg-forest py-20 text-ivory sm:py-28">
      <svg className="pointer-events-none absolute -left-32 bottom-0 h-[500px] w-[500px] text-forest-2" viewBox="0 0 400 400" aria-hidden="true">
        <path d="M0 400V60c90 10 170 70 200 160s110 150 200 180z" fill="currentColor" />
      </svg>
      <div className="container-x relative">
        <Reveal><SectionTitle eyebrow={s.eyebrow} title={s.title} light center /></Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Reveal key={i} delay={i * 0.08}>
              <figure data-testid={`testimonial-placeholder-${i}`} className="flex h-full flex-col rounded-[28px] border border-dashed border-gold-light/50 bg-ivory/[0.04] p-8">
                <span className="self-start rounded-full bg-gold-light/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold-light">{s.label}</span>
                <Quote size={30} className="mt-6 text-gold-light" strokeWidth={1.2} />
                <blockquote className="mt-4 flex-1 font-display text-xl italic leading-snug text-ivory/85">{s.text}</blockquote>
                <figcaption className="mt-8 flex items-center gap-3 border-t border-ivory/15 pt-5">
                  <span className="h-10 w-10 rounded-full border border-dashed border-ivory/40" />
                  <span>
                    <span className="block text-sm text-ivory/90">{s.who}</span>
                    <span className="block text-xs text-ivory/70">{s.whoSub}</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
